import React, { useState, useEffect, useRef } from 'react';
import { apiGet, apiPost } from '../utils/api';
import FormattedMessage from '../components/FormattedMessage';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';

interface GroupSettings {
  id?: string;
  chatId: string;
  type: string;
  title?: string | null;
  username?: string | null;
  loggingEnabled: boolean;
  logOnlyCommands: boolean;
  logOnlyMentions: boolean;
  botResponsesEnabled: boolean;
  respondOnlyToMentions: boolean;
  autoResponses: Record<string, string> | null;
  createdAt?: string;
  updatedAt?: string;
}

interface ChatContact {
  id: string;
  name: string;
  telegramUsername: string | null;
  telegramChatId: string | null;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount?: number;
  isUnregistered?: boolean;
  originalPlayerId?: string;
  isPrivateGroup?: boolean; // Флаг приватной группы
}

interface Message {
  id: string;
  text: string;
  direction: 'INCOMING' | 'OUTGOING';
  createdAt: string;
  status?: string;
  errorMessage?: string | null;
  failedAttempts?: number;
  type?: string;
  readAt?: string | null;
  threadId?: string | null;
  author?: {
    id?: number;
    username?: string;
    firstName?: string;
    lastName?: string;
    playerName?: string;
  };
}

interface GroupTopic {
  id: string;
  threadId: string;
  name: string | null;
  unreadCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

// Мемоизированный компонент для элемента контакта
const ChatContactItem = React.memo<{
  contact: ChatContact;
  isSelected: boolean;
  onSelect: (contact: ChatContact) => void;
  formatTime: (dateString: string) => string;
}>(({ contact, isSelected, onSelect, formatTime }) => {
  return (
    <div
      onClick={() => onSelect(contact)}
      className={`flex items-center p-1 cursor-pointer hover:bg-gray-50 border-b border-gray-100 ${
        isSelected ? 'bg-blue-50' : ''
      }`}
    >
      {/* Аватар */}
      <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold mr-2 flex-shrink-0">
        {contact.name.charAt(0).toUpperCase()}
      </div>

      {/* Информация о контакте */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <h3 className="font-medium text-sm text-gray-900 truncate">{contact.name}</h3>
            {contact.isPrivateGroup && (
              <svg
                className="w-3.5 h-3.5 text-gray-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <title>Приватная группа</title>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            )}
          </div>
          {contact.lastMessageTime && (
            <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
              {formatTime(contact.lastMessageTime)}
            </span>
          )}
        </div>
      </div>

      {/* Индикатор непрочитанных (показываем только если > 0) */}
      {(contact.unreadCount ?? 0) > 0 && (
        <div className="ml-1.5 bg-blue-500 text-white text-xs font-semibold rounded-full min-w-[18px] h-4 flex items-center justify-center flex-shrink-0 px-1">
          {contact.unreadCount! > 99 ? '99+' : contact.unreadCount}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Кастомная функция сравнения для оптимизации
  return (
    prevProps.contact.id === nextProps.contact.id &&
    prevProps.contact.name === nextProps.contact.name &&
    prevProps.contact.unreadCount === nextProps.contact.unreadCount &&
    prevProps.contact.lastMessageTime === nextProps.contact.lastMessageTime &&
    prevProps.contact.lastMessage === nextProps.contact.lastMessage &&
    prevProps.contact.isPrivateGroup === nextProps.contact.isPrivateGroup &&
    prevProps.isSelected === nextProps.isSelected
  );
});

ChatContactItem.displayName = 'ChatContactItem';

const HISTORY_LOOKBACK_OPTIONS = [7, 30, 90, 180, 365] as const;
const DEFAULT_HISTORY_LOOKBACK_DAYS = 30;

const getHistoryStartDate = (lookbackDays: number) => {
  const date = new Date();
  date.setDate(date.getDate() - lookbackDays);
  return date.toISOString();
};

const extractRetryAfterSeconds = (error: unknown): number => {
  const anyError = error as any;
  const direct = Number(anyError?.retryAfterSeconds);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const status = Number(anyError?.status);
  if (status !== 429) return 0;
  const match = String(anyError?.message || '').match(/(\d+)\s*sec/i);
  if (!match) return 60;
  const parsed = Number.parseInt(match[1], 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 60;
};

const FAILED_MERGE_WINDOW_MS = 3 * 60 * 1000;

const collapseFailedOutgoingMessages = (items: Message[]): Message[] => {
  const result: Message[] = [];

  for (const message of items) {
    const normalizedStatus = String(message.status || '').trim().toUpperCase();
    const isFailedOutgoing = message.direction === 'OUTGOING' && normalizedStatus === 'FAILED';

    if (!isFailedOutgoing) {
      result.push(message);
      continue;
    }

    const previous = result[result.length - 1];
    const prevStatus = String(previous?.status || '').trim().toUpperCase();
    const isPreviousFailedOutgoing = previous?.direction === 'OUTGOING' && prevStatus === 'FAILED';
    const sameText = String(previous?.text || '') === String(message.text || '');
    const sameType = String(previous?.type || '') === String(message.type || '');
    const sameThread = String(previous?.threadId || '') === String(message.threadId || '');
    const previousTime = previous ? new Date(previous.createdAt).getTime() : 0;
    const currentTime = new Date(message.createdAt).getTime();
    const isNearInTime =
      Number.isFinite(previousTime) &&
      Number.isFinite(currentTime) &&
      Math.abs(currentTime - previousTime) <= FAILED_MERGE_WINDOW_MS;

    if (isPreviousFailedOutgoing && sameText && sameType && sameThread && isNearInTime) {
      const mergedAttempts = (previous.failedAttempts || 1) + 1;
      result[result.length - 1] = {
        ...previous,
        id: `${previous.id}__retry_${mergedAttempts}`,
        createdAt: message.createdAt, // показываем время последней попытки
        failedAttempts: mergedAttempts,
        errorMessage: message.errorMessage || previous.errorMessage || null
      };
      continue;
    }

    result.push({
      ...message,
      failedAttempts: message.failedAttempts || 1
    });
  }

  return result;
};

const Chat: React.FC = () => {
  const [contacts, setContacts] = useState<ChatContact[]>([]);
  const [selectedContact, setSelectedContact] = useState<ChatContact | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [contactsRefreshInterval, setContactsRefreshInterval] = useState(30); // секунды
  const [messagesRefreshInterval, setMessagesRefreshInterval] = useState(10); // секунды
  const [historyLookbackDays, setHistoryLookbackDays] = useState(DEFAULT_HISTORY_LOOKBACK_DAYS);
  const [groupSettingsMap, setGroupSettingsMap] = useState<Map<string, GroupSettings>>(new Map());
  const [topics, setTopics] = useState<GroupTopic[]>([]);
  const [mainThreadUnreadCount, setMainThreadUnreadCount] = useState<number>(0);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null); // null = основной поток, string = threadId
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showRefreshSettingsModal, setShowRefreshSettingsModal] = useState(false);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const firstUnreadRef = React.useRef<HTMLDivElement>(null);
  const contactsListRef = React.useRef<HTMLDivElement>(null);
  const lastMarkedContactRef = React.useRef<string | null>(null);
  const lastMarkedTopicRef = React.useRef<string | null>(null);
  const isInitialLoadRef = React.useRef<boolean>(true);
  const firstUnreadMessageIdRef = React.useRef<string | null>(null);
  const scrollPositionRef = React.useRef<number>(0);
  const contactsRateLimitUntilRef = React.useRef<number>(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const historyStartDate = React.useMemo(() => getHistoryStartDate(historyLookbackDays), [historyLookbackDays]);

  // Установка стилей для предотвращения скроллбара на странице
  useEffect(() => {
    // Сохраняем оригинальные стили
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyHeight = document.body.style.height;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalHtmlHeight = document.documentElement.style.height;
    const originalRootStyle = document.getElementById('root')?.style.cssText || '';
    
    // Устанавливаем стили для предотвращения скроллбара
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100vh';
    
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.style.padding = '0';
      rootElement.style.margin = '0';
      rootElement.style.maxWidth = 'none';
      rootElement.style.height = '100vh';
      rootElement.style.overflow = 'hidden';
    }
    
    // Восстанавливаем стили при размонтировании
    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.height = originalBodyHeight;
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.documentElement.style.height = originalHtmlHeight;
      
      if (rootElement) {
        rootElement.style.cssText = originalRootStyle;
      }
    };
  }, []);

  // Загрузка настроек групп и настроек обновления
  useEffect(() => {
    fetchGroupSettings();
    fetchRefreshSettings();
  }, []);

  // Загрузка списка контактов (игроков с Telegram)
  // Загружаем контакты после загрузки настроек групп, чтобы использовать названия групп
  useEffect(() => {
    fetchContacts();
  }, [groupSettingsMap, historyStartDate]);

  // Восстанавливаем позицию прокрутки после обновления контактов
  useEffect(() => {
    if (scrollPositionRef.current > 0 && contactsListRef.current) {
      // Используем двойной requestAnimationFrame для гарантии, что DOM обновлен
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (contactsListRef.current) {
            contactsListRef.current.scrollTop = scrollPositionRef.current;
          }
        });
      });
    }
  }, [contacts]);

  useEffect(() => {
    // Автообновление списка контактов
    const interval = setInterval(() => {
      fetchContacts();
    }, contactsRefreshInterval * 1000);
    
    return () => clearInterval(interval);
  }, [contactsRefreshInterval, historyStartDate]);

  // Загрузка топиков при выборе группы
  useEffect(() => {
    if (selectedContact && selectedContact.telegramChatId) {
      const chatIdNum = parseInt(selectedContact.telegramChatId);
      const isGroup = !isNaN(chatIdNum) && chatIdNum < 0;
      
      if (isGroup) {
        // Сбрасываем выбранный топик при смене группы (основной поток по умолчанию)
        setSelectedTopicId(null);
        lastMarkedTopicRef.current = null; // Сбрасываем отметку при смене группы
        fetchTopics(selectedContact.telegramChatId);
      } else {
        setTopics([]);
        setSelectedTopicId(null);
        lastMarkedTopicRef.current = null;
      }
    } else {
      setTopics([]);
      setSelectedTopicId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact?.telegramChatId]);

  // Загрузка сообщений при выборе контакта или топика
  useEffect(() => {
    // Сбрасываем флаг первой загрузки при смене контакта или топика
    isInitialLoadRef.current = true;
    firstUnreadMessageIdRef.current = null;
    
    if (selectedContact) {
      // Определяем, является ли это группой
      const chatId = selectedContact.telegramChatId;
      const chatIdNum = chatId ? parseInt(chatId) : NaN;
      const isGroup = !isNaN(chatIdNum) && chatIdNum < 0;
      
      // Для групп всегда передаем threadId:
      // - null для основного потока (сообщения без топика)
      // - конкретный threadId для выбранного топика
      // Для приватных чатов не передаем threadId (undefined)
      let threadId: string | null | undefined;
      if (isGroup) {
        // Если selectedTopicId === null, передаем null (основной поток)
        // Если selectedTopicId - строка, передаем эту строку (конкретный топик)
        threadId = selectedTopicId;
      } else {
        // Для приватных чатов не фильтруем по threadId
        threadId = undefined;
      }
      
      // Определяем playerId для загрузки сообщений
      let playerIdForMessages = selectedContact.id;
      if (selectedContact.originalPlayerId) {
        playerIdForMessages = selectedContact.originalPlayerId;
      } else if (selectedContact.id.includes('_')) {
        playerIdForMessages = selectedContact.id.split('_')[0];
      }
      
      const chatIdForMessages: string | undefined = chatId !== null && chatId !== undefined ? (chatId as string) : undefined;
      fetchMessages(playerIdForMessages, chatIdForMessages, threadId);
      
      // Автообновление сообщений
      const interval = setInterval(() => {
        let playerIdForMessages = selectedContact.id;
        if (selectedContact.originalPlayerId) {
          playerIdForMessages = selectedContact.originalPlayerId;
        } else if (selectedContact.id.includes('_')) {
          playerIdForMessages = selectedContact.id.split('_')[0];
        }
        const chatIdForMessages: string | undefined = chatId !== null && chatId !== undefined ? (chatId as string) : undefined;
        fetchMessages(playerIdForMessages, chatIdForMessages, threadId);
      }, messagesRefreshInterval * 1000);
      
      return () => clearInterval(interval);
    }
  }, [selectedContact, selectedTopicId, messagesRefreshInterval, historyStartDate]);


  // Автопрокрутка к первому непрочитанному сообщению (при первой загрузке) или к последнему (при обновлениях)
  useEffect(() => {
    if (messages.length === 0) return;
    
    // При первой загрузке ищем первое непрочитанное сообщение
    if (isInitialLoadRef.current) {
      // Находим первое непрочитанное входящее сообщение
      const firstUnread = messages.find(msg => 
        msg.direction === 'INCOMING' && !msg.readAt
      );
      
      if (firstUnread) {
        // Сохраняем ID первого непрочитанного сообщения
        firstUnreadMessageIdRef.current = firstUnread.id;
        // Прокручиваем к первому непрочитанному сообщению
        setTimeout(() => {
          firstUnreadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
        isInitialLoadRef.current = false;
      } else {
        // Если непрочитанных нет, прокручиваем к последнему сообщению
        firstUnreadMessageIdRef.current = null;
        scrollToBottom();
        isInitialLoadRef.current = false;
      }
    } else {
      // При последующих обновлениях прокручиваем к последнему сообщению
      firstUnreadMessageIdRef.current = null;
      scrollToBottom();
    }
  }, [messages]);

  const fetchContacts = async () => {
    if (contactsRateLimitUntilRef.current > Date.now()) {
      return;
    }

    const isInitialLoad = contacts.length === 0;
    
    // Сохраняем позицию прокрутки перед обновлением
    if (!isInitialLoad && contactsListRef.current) {
      scrollPositionRef.current = contactsListRef.current.scrollTop;
    }
    
    try {
      // Устанавливаем loading только при первой загрузке
      if (isInitialLoad) {
        setLoading(true);
      }

      contactsRateLimitUntilRef.current = 0;

      if (isInitialLoad) {
        const summaryData = await apiGet<{ contacts: ChatContact[] }>(
          `/telegram-messages/contacts-summary?startDate=${encodeURIComponent(historyStartDate)}`
        );
        const nextContacts = Array.isArray(summaryData?.contacts) ? summaryData.contacts : [];
        setContacts(nextContacts);
        return;
      }

      // Легковесное обновление для периодического авто-рефреша:
      // не делаем сотни запросов по каждому chatId, а подтягиваем
      // свежую ленту и обновляем только lastMessage/lastMessageTime/unreadCount.
      if (!isInitialLoad) {
        const recentData = await apiGet<{ messages: any[]; pagination: any }>(
          `/telegram-messages?limit=800&page=1&startDate=${encodeURIComponent(historyStartDate)}`
        );

        const latestByChat = new Map<string, any>();
        const unreadByChat = new Map<string, number>();

        (recentData.messages || []).forEach((msg: any) => {
          const chatId = String(msg?.chatId || '').trim();
          if (!chatId) return;

          const prev = latestByChat.get(chatId);
          if (!prev || new Date(msg.createdAt).getTime() > new Date(prev.createdAt).getTime()) {
            latestByChat.set(chatId, msg);
          }

          if (msg.direction === 'INCOMING' && !msg.readAt) {
            unreadByChat.set(chatId, (unreadByChat.get(chatId) || 0) + 1);
          }
        });

        setContacts((prevContacts) =>
          prevContacts
            .map((contact) => {
              const chatId = String(contact.telegramChatId || '').trim();
              if (!chatId) return contact;
              const latest = latestByChat.get(chatId);
              const nextUnread = unreadByChat.get(chatId) || 0;
              if (!latest) {
                if ((contact.unreadCount || 0) === nextUnread) return contact;
                return { ...contact, unreadCount: nextUnread };
              }

              const nextLastMessage = latest.text || '';
              const nextLastMessageTime = latest.createdAt || '';
              if (
                contact.lastMessage === nextLastMessage &&
                contact.lastMessageTime === nextLastMessageTime &&
                (contact.unreadCount || 0) === nextUnread
              ) {
                return contact;
              }

              return {
                ...contact,
                lastMessage: nextLastMessage,
                lastMessageTime: nextLastMessageTime,
                unreadCount: nextUnread
              };
            })
            .sort((a, b) => {
              if (!a.lastMessageTime && !b.lastMessageTime) return 0;
              if (!a.lastMessageTime) return 1;
              if (!b.lastMessageTime) return -1;
              return new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
            })
        );

        return;
      }
      
      // Загружаем все привязанные Telegram аккаунты из таблицы PlayerTelegramAccount
      const telegramAccountsData = await apiGet<{ accounts: any[] }>('/players/telegram-accounts/all');
      const telegramAccounts = telegramAccountsData.accounts || [];
      
      console.log('Загружено Telegram аккаунтов:', telegramAccounts.length);
      
      // Создаем контакты из привязанных Telegram аккаунтов
      // Для каждого аккаунта создаем отдельный контакт.
      // Важно: обрабатываем батчами, чтобы не перегружать API сотнями одновременных запросов.
      const contactsWithMessages: any[] = [];
      const contactsBatchSize = 10;
      for (let i = 0; i < telegramAccounts.length; i += contactsBatchSize) {
        const batch = telegramAccounts.slice(i, i + contactsBatchSize);
        const batchResults = await Promise.all(
          batch.map(async (account: any) => {
          try {
            const player = account.player;
            if (!player) return null;
            
            // Загружаем последнее сообщение для этого chatId
            const messagesData = await apiGet<{ messages: any[]; pagination: any }>(
              `/telegram-messages?chatId=${account.chatId}&limit=1&page=1&startDate=${encodeURIComponent(historyStartDate)}`
            );
            
            const lastMsg = messagesData.messages?.[0];
            
            // Подсчет непрочитанных делаем одним легким запросом:
            // unreadOnly=true + limit=1, реальное число берем из pagination.total.
            const unreadData = await apiGet<{ messages: any[]; pagination: any }>(
              `/telegram-messages?chatId=${account.chatId}&direction=INCOMING&unreadOnly=true&limit=1&page=1&startDate=${encodeURIComponent(historyStartDate)}`
            );
            const unreadCount = unreadData.pagination?.total || 0;
            
            // Проверяем, является ли это группой (chatId < 0 для групп в Telegram)
            const chatIdNum = parseInt(account.chatId);
            const isGroup = !isNaN(chatIdNum) && chatIdNum < 0;
            
            // Извлекаем информацию о группе из метаданных сообщения
            let chatTitle = null;
            let chatUsername = null;
            if (lastMsg?.metadata) {
              try {
                const meta = JSON.parse(lastMsg.metadata);
                const chatInfo = meta.message?.chat || meta.callback_query?.message?.chat;
                if (chatInfo) {
                  chatTitle = chatInfo.title || null;
                  chatUsername = chatInfo.username || null;
                }
              } catch (e) {
                // Игнорируем ошибки парсинга
              }
            }
            
            // ПРИОРИТЕТ 1: Сначала проверяем, является ли это группой
            // Формируем имя для отображения
            let displayName = player.name;
            let isPrivateGroup = false;
            
            if (isGroup) {
              // Для групп используем приоритет:
              // 1. title из настроек группы
              // 2. title из метаданных сообщения
              // 3. username из настроек группы
              // 4. username из метаданных сообщения
              // 5. имя игрока как fallback
              const groupSettings = groupSettingsMap.get(account.chatId);
              if (groupSettings?.title) {
                displayName = groupSettings.title;
                // Группа считается приватной, если у неё нет username
                isPrivateGroup = !groupSettings.username && !account.username;
              } else if (chatTitle) {
                displayName = chatTitle;
                isPrivateGroup = !chatUsername && !account.username;
              } else if (groupSettings?.username) {
                displayName = `@${groupSettings.username}`;
              } else if (chatUsername) {
                displayName = `@${chatUsername}`;
              } else {
                // Если названия нет нигде, используем имя игрока как fallback
                displayName = player.name;
                isPrivateGroup = !account.username;
              }
            } else {
              // Для приватных чатов используем имя игрока
              // Для идентифицированных контактов не показываем username в скобках
              displayName = player.name;
            }
            
            return {
              id: `${player.id}_${account.chatId}`, // Уникальный ID: playerId_chatId
              name: displayName,
              telegramUsername: account.username || null,
              telegramChatId: account.chatId,
              lastMessage: lastMsg?.text || '',
              lastMessageTime: lastMsg?.createdAt || '',
              unreadCount: unreadCount,
              originalPlayerId: player.id, // Сохраняем ID игрока для отправки сообщений
              isPrivateGroup: isPrivateGroup // Флаг приватной группы
            };
            } catch (error) {
              console.error(`Ошибка загрузки сообщений для аккаунта ${account.chatId}:`, error);
              return null;
            }
          })
        );
        contactsWithMessages.push(...batchResults);
      }
      
      // Фильтруем null значения
      const validContacts: ChatContact[] = contactsWithMessages.filter((contact) => contact !== null) as ChatContact[];
      
      // Группируем контакты по chatId для групп (чтобы избежать дубликатов)
      // Для приватных чатов оставляем отдельные контакты для каждого игрока
      const contactsMap = new Map<string, ChatContact>();
      
      validContacts.forEach(contact => {
        if (!contact.telegramChatId) return;
        
        const chatIdNum = parseInt(contact.telegramChatId);
        const isGroup = !isNaN(chatIdNum) && chatIdNum < 0;
        
        if (isGroup) {
          // Для групп: один контакт на chatId (берем первый с самым свежим сообщением)
          if (!contactsMap.has(contact.telegramChatId)) {
            // Для групп используем chatId как ID контакта
            contactsMap.set(contact.telegramChatId, {
              ...contact,
              id: `group_${contact.telegramChatId}` // Уникальный ID для группы
            });
          } else {
            // Если уже есть контакт с этим chatId, выбираем тот, у которого более свежее сообщение
            const existing = contactsMap.get(contact.telegramChatId)!;
            const existingTime = existing.lastMessageTime ? new Date(existing.lastMessageTime).getTime() : 0;
            const newTime = contact.lastMessageTime ? new Date(contact.lastMessageTime).getTime() : 0;
            
            if (newTime > existingTime) {
              // Обновляем контакт, но сохраняем максимальный unreadCount и правильный ID
              contactsMap.set(contact.telegramChatId, {
                ...contact,
                id: `group_${contact.telegramChatId}`, // Уникальный ID для группы
                unreadCount: Math.max(contact.unreadCount || 0, existing.unreadCount || 0)
              });
            } else {
              // Обновляем unreadCount существующего контакта
              existing.unreadCount = Math.max(contact.unreadCount || 0, existing.unreadCount || 0);
            }
          }
        } else {
          // Для приватных чатов: используем уникальный ID (playerId_chatId)
          contactsMap.set(contact.id, contact);
        }
      });
      
      // Преобразуем Map обратно в массив
      const deduplicatedContacts = Array.from(contactsMap.values());
      
      // Создаем Set с chatId, которые уже есть в списке контактов
      const existingChatIds = new Set(deduplicatedContacts.map(c => c.telegramChatId).filter(id => id !== null));

      // Находим контакты, которых нет в PlayerTelegramAccount, но есть сообщения
      // Загружаем все сообщения и находим уникальные chatId (с пагинацией)
      try {
        // Загружаем все сообщения постранично
        let allMessages: any[] = [];
        let page = 1;
        let hasMore = true;
        const pageSize = 1000;
        
        while (hasMore) {
          const pageData = await apiGet<{ messages: any[]; pagination: any }>(
            `/telegram-messages?limit=${pageSize}&page=${page}&startDate=${encodeURIComponent(historyStartDate)}`
          );
          allMessages = allMessages.concat(pageData.messages || []);
          hasMore = pageData.messages && pageData.messages.length === pageSize;
          page++;
        }
        
        const allMessagesData = { messages: allMessages, pagination: { total: allMessages.length } };
        
        // Группируем сообщения по chatId и playerId
        const chatIdPlayerMap = new Map<string, { playerId: string; playerName: string; messages: any[] }>();
        
        allMessagesData.messages?.forEach((msg: any) => {
          if (msg.chatId && msg.player?.id) {
            // Пропускаем системного игрока (он обрабатывается отдельно)
            if (msg.player.name === 'Незарегистрированные пользователи (Telegram)') {
              return;
            }
            
            // Пропускаем, если уже есть в списке контактов
            if (existingChatIds.has(msg.chatId)) {
              return;
            }
            
            const key = `${msg.chatId}_${msg.player.id}`;
            if (!chatIdPlayerMap.has(key)) {
              chatIdPlayerMap.set(key, {
                playerId: msg.player.id,
                playerName: msg.player.name,
                messages: []
              });
            }
            chatIdPlayerMap.get(key)!.messages.push(msg);
          }
        });
        
        // Создаем контакты для найденных chatId, которых нет в PlayerTelegramAccount
        const missingContacts = await Promise.all(
          Array.from(chatIdPlayerMap.entries()).map(async ([key, data]) => {
            try {
              const { playerId, playerName, messages } = data;
              const chatId = key.split('_')[0];
              
              // Сортируем сообщения по времени
              const sortedMessages = [...messages].sort((a, b) => 
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
              );
              const lastMsg = sortedMessages[0];
              
              // ПРИОРИТЕТ 1: Проверяем, является ли это группой (chatId < 0 для групп в Telegram)
              const chatIdNum = parseInt(chatId);
              const isGroup = !isNaN(chatIdNum) && chatIdNum < 0;
              
              // Извлекаем информацию о группе и пользователе из метаданных сообщения
              let chatTitle = null;
              let chatUsername = null;
              let chatType = null;
              let username = null;
              let firstName = null;
              let lastName = null;
              
              for (const msg of messages) {
                try {
                  if (msg.metadata) {
                    const meta = JSON.parse(msg.metadata);
                    const chatInfo = meta.message?.chat || meta.callback_query?.message?.chat;
                    if (chatInfo) {
                      chatTitle = chatTitle || chatInfo.title || null;
                      chatUsername = chatUsername || chatInfo.username || null;
                      chatType = chatType || chatInfo.type || null;
                    }
                    
                    // Извлекаем информацию о пользователе для приватных чатов
                    const from = meta.message?.from || meta.callback_query?.from || meta.edited_message?.from;
                    if (from) {
                      username = username || from.username || null;
                      firstName = firstName || from.first_name || null;
                      lastName = lastName || from.last_name || null;
                    }
                    
                    if (meta.userInfo) {
                      username = username || meta.userInfo.username || null;
                      firstName = firstName || meta.userInfo.firstName || null;
                      lastName = lastName || meta.userInfo.lastName || null;
                    }
                  }
                } catch (e) {
                  // Игнорируем ошибки парсинга
                }
              }
              
              // Формируем имя для отображения
              let displayName = playerName;
              let isPrivateGroup = false;
              
              // СНАЧАЛА проверяем, является ли это группой
              if (isGroup || chatType === 'group' || chatType === 'supergroup' || chatType === 'channel') {
                // Для групп используем приоритет:
                // 1. title из настроек группы
                // 2. title из метаданных сообщения
                // 3. username из настроек группы
                // 4. username из метаданных сообщения
                // 5. имя игрока как fallback (только если нет никакой информации о группе)
                const groupSettings = groupSettingsMap.get(chatId);
                if (groupSettings?.title) {
                  displayName = groupSettings.title;
                  isPrivateGroup = !groupSettings.username && !chatUsername;
                } else if (chatTitle) {
                  displayName = chatTitle;
                  isPrivateGroup = !chatUsername;
                } else if (groupSettings?.username) {
                  displayName = `@${groupSettings.username}`;
                } else if (chatUsername) {
                  displayName = `@${chatUsername}`;
                } else {
                  // Если названия нет нигде, используем имя игрока как fallback
                  displayName = playerName;
                  isPrivateGroup = true;
                }
              } else {
                // ПРИОРИТЕТ 2: Только если это НЕ группа, используем имя контакта/игрока
                // Для незарегистрированных пользователей: Chat ID + Telegram username
                // Для идентифицированных: имя игрока из базы данных
                
                // Проверяем, является ли это незарегистрированным пользователем
                if (!playerName || playerName === 'Незарегистрированные пользователи (Telegram)') {
                  // Для незарегистрированных: Chat ID + Telegram username
                  if (chatId && username) {
                    displayName = `${chatId} @${username}`;
                  } else if (chatId) {
                    displayName = chatId;
                  } else if (username) {
                    displayName = `@${username}`;
                  } else {
                    displayName = 'Неизвестный';
                  }
                } else {
                  // Для идентифицированных используем имя игрока из базы данных
                  displayName = playerName;
                }
              }
              
              // Подсчитываем непрочитанные входящие сообщения
              const unreadCount = messages.filter((msg: any) => 
                msg.direction === 'INCOMING' && !msg.readAt
              ).length;
              
              return {
                id: `${playerId}_${chatId}`,
                name: displayName,
                telegramUsername: chatUsername || (username ? username : null),
                telegramChatId: chatId,
                lastMessage: lastMsg?.text || '',
                lastMessageTime: lastMsg?.createdAt || '',
                unreadCount: unreadCount,
                originalPlayerId: playerId,
                isPrivateGroup: isPrivateGroup
              } as ChatContact;
            } catch (error) {
              console.error(`Ошибка создания контакта для ${key}:`, error);
              return null;
            }
          })
        );
        
        // Фильтруем null и добавляем к списку контактов
        const validMissingContacts = missingContacts.filter((contact) => contact !== null) as ChatContact[];
        
        // Добавляем недостающие контакты, избегая дубликатов для групп
        validMissingContacts.forEach(contact => {
          if (!contact.telegramChatId) return;
          
          const chatIdNum = parseInt(contact.telegramChatId);
          const isGroup = !isNaN(chatIdNum) && chatIdNum < 0;
          
          if (isGroup) {
            // Для групп: проверяем, нет ли уже контакта с этим chatId
            const existing = deduplicatedContacts.find(c => c.telegramChatId === contact.telegramChatId);
            if (!existing) {
              // Для групп используем chatId как ID контакта
              deduplicatedContacts.push({
                ...contact,
                id: `group_${contact.telegramChatId}` // Уникальный ID для группы
              });
            } else {
              // Обновляем unreadCount и lastMessageTime, если новое сообщение свежее
              const existingTime = existing.lastMessageTime ? new Date(existing.lastMessageTime).getTime() : 0;
              const newTime = contact.lastMessageTime ? new Date(contact.lastMessageTime).getTime() : 0;
              
              if (newTime > existingTime) {
                existing.lastMessageTime = contact.lastMessageTime;
                existing.lastMessage = contact.lastMessage;
              }
              existing.unreadCount = Math.max(existing.unreadCount || 0, contact.unreadCount || 0);
            }
          } else {
            // Для приватных чатов: проверяем по уникальному ID
            const existing = deduplicatedContacts.find(c => c.id === contact.id);
            if (!existing) {
              deduplicatedContacts.push(contact);
            }
          }
        });
      } catch (error) {
        console.error('Ошибка загрузки недостающих контактов:', error);
      }

      // Для системного игрока (незарегистрированные пользователи) создаем виртуальные контакты по chatId
      // Загружаем системного игрока отдельно
      try {
        const systemPlayerResponse = await apiGet<any>('/players?email=system@telegram.unregistered');
        const systemPlayer = Array.isArray(systemPlayerResponse) 
          ? systemPlayerResponse.find((p: any) => p.email === 'system@telegram.unregistered')
          : (systemPlayerResponse?.email === 'system@telegram.unregistered' ? systemPlayerResponse : null);
        
        if (systemPlayer) {
          // Загружаем все сообщения системного игрока (с пагинацией)
          let systemMessages: any[] = [];
          let page = 1;
          let hasMore = true;
          const pageSize = 1000;
          
          while (hasMore) {
            const pageData = await apiGet<{ messages: any[]; pagination: any }>(
              `/telegram-messages?playerId=${systemPlayer.id}&limit=${pageSize}&page=${page}&startDate=${encodeURIComponent(historyStartDate)}`
            );
            systemMessages = systemMessages.concat(pageData.messages || []);
            hasMore = pageData.messages && pageData.messages.length === pageSize;
            page++;
          }
          
          const systemMessagesData = { messages: systemMessages, pagination: { total: systemMessages.length } };
          
          // Группируем по chatId
          const chatIdGroups: Record<string, any[]> = {};
          systemMessagesData.messages?.forEach((msg: any) => {
            const chatId = msg.chatId;
            if (chatId) {
              if (!chatIdGroups[chatId]) {
                chatIdGroups[chatId] = [];
              }
              chatIdGroups[chatId].push(msg);
            }
          });

          // Создаем виртуальные контакты только для групп (не для приватных чатов)
          const unregisteredContacts = Object.entries(chatIdGroups)
            .map(([chatId, messages]) => {
              // Сортируем сообщения по времени
              const sortedMessages = [...messages].sort((a, b) => 
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
              );
              const lastMsg = sortedMessages[0];
              
              // Извлекаем информацию из метаданных
              let chatType = 'private';
              let chatTitle = null;
              let chatUsername = null;
              let username = null;
              
              try {
                if (lastMsg.metadata) {
                  const meta = JSON.parse(lastMsg.metadata);
                  const chatInfo = meta.message?.chat || meta.callback_query?.message?.chat;
                  
                  if (chatInfo) {
                    chatType = chatInfo.type || 'private';
                    chatTitle = chatInfo.title || null;
                    chatUsername = chatInfo.username || null;
                  }
                  
                  if (meta.userInfo?.username) {
                    username = meta.userInfo.username;
                  }
                }
              } catch (e) {
                // Игнорируем ошибки парсинга
              }

              // Получаем настройки группы, если это группа
              const groupSettings = groupSettingsMap.get(chatId);
              const isGroup = chatType !== 'private';
              
              // Показываем и приватные чаты, и группы от незарегистрированных пользователей
              // (ранее пропускали приватные чаты, но теперь показываем все)
              
              // Определяем имя для отображения
              let displayName = chatId; // По умолчанию Chat ID
              let isPrivateGroup = false;
              
              if (isGroup) {
                // Для групп используем приоритет:
                // 1. title из настроек группы
                // 2. username из настроек группы
                // 3. title из метаданных сообщения
                // 4. username из метаданных сообщения
                if (groupSettings?.title) {
                  displayName = groupSettings.title;
                  // Группа считается приватной, если у неё нет username
                  isPrivateGroup = !groupSettings.username && !chatUsername;
                } else if (groupSettings?.username) {
                  displayName = `@${groupSettings.username}`;
                } else if (chatTitle) {
                  displayName = chatTitle;
                  isPrivateGroup = !chatUsername;
                } else if (chatUsername) {
                  displayName = `@${chatUsername}`;
                }
              } else {
                // Для приватных чатов незарегистрированных пользователей:
                // Формат: "Chat ID + Telegram username" (если есть оба)
                // Или только один из них, если другого нет
                let username = null;
                
                // Извлекаем username из всех сообщений (ищем в любом сообщении)
                for (const msg of messages) {
                  try {
                    if (msg.metadata) {
                      const meta = JSON.parse(msg.metadata);
                      if (meta.userInfo) {
                        username = meta.userInfo.username || username;
                      } else if (meta.message?.from) {
                        username = meta.message.from.username || username;
                      }
                    }
                    if (username) break;
                  } catch (e) {
                    // Игнорируем ошибки парсинга
                  }
                }
                
                // Формируем имя: Chat ID + Telegram username
                if (chatId && username) {
                  displayName = `${chatId} @${username}`;
                } else if (chatId) {
                  displayName = chatId;
                } else if (username) {
                  displayName = `@${username}`;
                } else {
                  displayName = 'Неизвестный';
                }
              }

              const unreadCount = messages.filter((msg: any) => 
                msg.direction === 'INCOMING' && !msg.readAt
              ).length;

              // Определяем ID контакта: для групп используем chatId, для приватных чатов - playerId_chatId
              const contactId = isGroup 
                ? `group_${chatId}` 
                : `${systemPlayer.id}_${chatId}`;
              
              return {
                id: contactId,
                name: displayName,
                telegramUsername: groupSettings?.username || chatUsername || null,
                telegramChatId: chatId,
                lastMessage: lastMsg?.text || '',
                lastMessageTime: lastMsg?.createdAt || '',
                unreadCount: unreadCount,
                isUnregistered: true,
                originalPlayerId: systemPlayer.id,
                isPrivateGroup: isPrivateGroup // Флаг приватной группы
              };
            })
            .filter((contact) => contact !== null) as ChatContact[]; // Убираем null значения

          // Добавляем виртуальные контакты к списку, избегая дубликатов для групп
          unregisteredContacts.forEach(contact => {
            if (!contact.telegramChatId) return;
            
            const chatIdNum = parseInt(contact.telegramChatId);
            const isGroup = !isNaN(chatIdNum) && chatIdNum < 0;
            
            if (isGroup) {
              // Для групп: проверяем, нет ли уже контакта с этим chatId
              const existing = deduplicatedContacts.find(c => c.telegramChatId === contact.telegramChatId);
              if (!existing) {
                // Для групп используем chatId как ID контакта
                deduplicatedContacts.push({
                  ...contact,
                  id: `group_${contact.telegramChatId}` // Уникальный ID для группы
                });
              } else {
                // Обновляем unreadCount и lastMessageTime, если новое сообщение свежее
                const existingTime = existing.lastMessageTime ? new Date(existing.lastMessageTime).getTime() : 0;
                const newTime = contact.lastMessageTime ? new Date(contact.lastMessageTime).getTime() : 0;
                
                if (newTime > existingTime) {
                  existing.lastMessageTime = contact.lastMessageTime;
                  existing.lastMessage = contact.lastMessage;
                }
                existing.unreadCount = Math.max(existing.unreadCount || 0, contact.unreadCount || 0);
              }
            } else {
              // Для приватных чатов: проверяем по уникальному ID
              const existing = deduplicatedContacts.find(c => c.id === contact.id);
              if (!existing) {
                deduplicatedContacts.push(contact);
              }
            }
          });
        }
      } catch (error) {
        console.error('Ошибка загрузки незарегистрированных пользователей:', error);
      }

      // Сортируем по времени последнего сообщения
      deduplicatedContacts.sort((a: ChatContact, b: ChatContact) => {
        if (!a.lastMessageTime && !b.lastMessageTime) return 0;
        if (!a.lastMessageTime) return 1;
        if (!b.lastMessageTime) return -1;
        return new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
      });

      console.log('Загружено контактов:', deduplicatedContacts.length);
      
      // Оптимизация: обновляем состояние только если данные действительно изменились
      setContacts(prevContacts => {
        // Быстрая проверка по длине
        if (prevContacts.length !== deduplicatedContacts.length) {
          return deduplicatedContacts;
        }
        
        // Проверяем, изменились ли ID контактов
        const prevIds = new Set(prevContacts.map(c => c.id));
        const newIds = new Set(deduplicatedContacts.map(c => c.id));
        
        if (prevIds.size !== newIds.size || 
            !Array.from(prevIds).every(id => newIds.has(id))) {
          return deduplicatedContacts;
        }
        
        // Проверяем, изменились ли данные существующих контактов
        // И сохраняем ссылки на старые объекты, если данные не изменились
        const contactsMap = new Map(deduplicatedContacts.map(c => [c.id, c]));
        const prevContactsMap = new Map(prevContacts.map(c => [c.id, c]));
        
        let hasChanges = false;
        const optimizedContacts = deduplicatedContacts.map(newContact => {
          const prevContact = prevContactsMap.get(newContact.id);
          
          // Если контакт не изменился, сохраняем ссылку на старый объект
          // ВАЖНО: Если unreadCount был обнулен локально (0), но в новых данных он больше 0,
          // это может означать, что сообщения были отмечены как прочитанные, но при обновлении
          // счетчик пересчитался. В этом случае сохраняем локальное значение 0.
          if (prevContact) {
            const newUnreadCount = newContact.unreadCount ?? 0;
            // Если локальный счетчик был 0 (сообщения отмечены как прочитанные),
            // а новый счетчик больше 0, сохраняем 0 (не перезаписываем)
            const unreadCount = (prevContact.unreadCount === 0 && newUnreadCount > 0) 
              ? 0 
              : newUnreadCount;
            
            if (prevContact.name === newContact.name &&
                prevContact.unreadCount === unreadCount &&
                prevContact.lastMessageTime === newContact.lastMessageTime &&
                prevContact.lastMessage === newContact.lastMessage &&
                prevContact.isPrivateGroup === newContact.isPrivateGroup) {
              return prevContact; // Возвращаем старый объект для сохранения ссылки
            }
            
            // Если изменился только unreadCount (и он был 0), обновляем только его
            if (prevContact.name === newContact.name &&
                prevContact.lastMessageTime === newContact.lastMessageTime &&
                prevContact.lastMessage === newContact.lastMessage &&
                prevContact.isPrivateGroup === newContact.isPrivateGroup &&
                prevContact.unreadCount === 0 && newUnreadCount > 0) {
              // Сохраняем 0, так как сообщения уже отмечены как прочитанные
              return { ...prevContact, unreadCount: 0 };
            }
          }
          
          hasChanges = true;
          // Применяем логику сохранения unreadCount = 0
          const newUnreadCount = newContact.unreadCount ?? 0;
          const unreadCount = (prevContact?.unreadCount === 0 && newUnreadCount > 0) 
            ? 0 
            : newUnreadCount;
          return { ...newContact, unreadCount };
        });
        
        return hasChanges ? optimizedContacts : prevContacts;
      });
    } catch (error) {
      console.error('Ошибка загрузки контактов:', error);
      const retryAfterSeconds = extractRetryAfterSeconds(error);
      if (retryAfterSeconds > 0) {
        contactsRateLimitUntilRef.current = Date.now() + retryAfterSeconds * 1000;
      }
    } finally {
      // Устанавливаем loading в false только при первой загрузке
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  const fetchTopics = async (chatId: string) => {
    try {
      const response = await apiGet<{ topics: GroupTopic[]; mainThreadUnreadCount: number }>(`/telegram-group-settings/${chatId}/topics`);
      setTopics(response.topics || []);
      setMainThreadUnreadCount(response.mainThreadUnreadCount || 0);
      
      // Проверяем, что выбранный топик все еще существует
      if (selectedTopicId !== null) {
        const topicExists = (response.topics || []).some(t => t.threadId === selectedTopicId);
        if (!topicExists) {
          setSelectedTopicId(null);
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки топиков:', error);
      setTopics([]);
      setMainThreadUnreadCount(0);
      setSelectedTopicId(null);
    }
  };

  const fetchMessages = async (playerId: string, chatId?: string | null, threadId?: string | null) => {
    try {
      let data;
      
      console.log('fetchMessages called with:', { playerId, chatId, threadId, selectedContact: selectedContact?.name });
      
      // Определяем, является ли это группой
      const chatIdNum = chatId ? parseInt(chatId) : NaN;
      const isGroup = !isNaN(chatIdNum) && chatIdNum < 0;
      
      // Для групп всегда загружаем сообщения по chatId (без playerId), так как сообщения из групп
      // могут быть привязаны к системному игроку или к разным игрокам
      if (isGroup && chatId) {
        let url = `/telegram-messages?chatId=${chatId}&limit=200&page=1&startDate=${encodeURIComponent(historyStartDate)}`;
        if (threadId !== undefined) {
          url += `&threadId=${threadId === null ? 'null' : threadId}`;
        }
        console.log('Loading group messages:', url);
        data = await apiGet<{ messages: any[]; pagination: any }>(url);
      } else if (chatId) {
        // Для приватных чатов всегда загружаем по chatId (это самый надежный способ)
        let url = `/telegram-messages?chatId=${chatId}&limit=200&page=1&startDate=${encodeURIComponent(historyStartDate)}`;
        if (threadId !== undefined) {
          url += `&threadId=${threadId === null ? 'null' : threadId}`;
        }
        console.log('Loading private chat messages by chatId:', url);
        data = await apiGet<{ messages: any[]; pagination: any }>(url);
      } else if (playerId.includes('_')) {
        // Если playerId содержит '_', это может быть либо незарегистрированный пользователь, либо привязанный аккаунт
        const [actualPlayerId] = playerId.split('_');
        
        // Проверяем, является ли это незарегистрированным пользователем
        if (selectedContact?.isUnregistered) {
          // Загружаем сообщения системного игрока и фильтруем по chatId и threadId
          let url = `/telegram-messages?playerId=${actualPlayerId}&limit=1000&page=1&startDate=${encodeURIComponent(historyStartDate)}`;
          if (threadId !== undefined) {
            url += `&threadId=${threadId === null ? 'null' : threadId}`;
          }
          console.log('Loading unregistered user messages:', url);
          data = await apiGet<{ messages: any[]; pagination: any }>(url);
        } else {
          // Это привязанный аккаунт, но нет chatId - используем playerId
          let url = `/telegram-messages?playerId=${actualPlayerId}&limit=200&page=1&startDate=${encodeURIComponent(historyStartDate)}`;
          if (threadId !== undefined) {
            url += `&threadId=${threadId === null ? 'null' : threadId}`;
          }
          console.log('Loading messages by playerId (fallback):', url);
          data = await apiGet<{ messages: any[]; pagination: any }>(url);
        }
      } else {
        // Старый формат (для обратной совместимости) - загружаем по playerId
        let url = `/telegram-messages?playerId=${playerId}&limit=200&page=1&startDate=${encodeURIComponent(historyStartDate)}`;
        if (chatId) {
          url += `&chatId=${chatId}`;
        }
        if (threadId !== undefined) {
          url += `&threadId=${threadId === null ? 'null' : threadId}`;
        }
        console.log('Loading messages by playerId (legacy):', url);
        data = await apiGet<{ messages: any[]; pagination: any }>(url);
      }
      
      console.log('Messages loaded:', data.messages?.length || 0);
      
      // Сортируем сообщения по времени (от старых к новым)
      const sortedMessages = [...(data.messages || [])].sort((a, b) => 
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      
      const formattedMessages = sortedMessages.map((msg: any) => {
        // Извлекаем информацию об авторе из метаданных
        let author: {
          id?: number;
          username?: string;
          firstName?: string;
          lastName?: string;
          playerName?: string;
        } | undefined = undefined;
        try {
          if (msg.metadata) {
            const meta = JSON.parse(msg.metadata);
            const from = meta.message?.from || meta.callback_query?.from || meta.edited_message?.from;
            
            if (from) {
              author = {
                id: from.id,
                username: from.username,
                firstName: from.first_name,
                lastName: from.last_name
              };
            }
          }
        } catch (e) {
          // Игнорируем ошибки парсинга
        }
        
        // Если есть связанный игрок, используем его имя
        // НО: если это системный игрок (незарегистрированные пользователи), не используем его имя
        // Вместо этого полагаемся на информацию из метаданных Telegram
        // Проверяем по имени игрока, так как email может не быть в ответе API
        if (msg.player && msg.player.name !== 'Незарегистрированные пользователи (Telegram)') {
          if (!author) {
            author = {
              playerName: msg.player.name
            };
          } else {
            author.playerName = msg.player.name;
          }
        }
        
        return {
          id: msg.id,
          text: msg.text || '',
          direction: msg.direction,
          createdAt: msg.createdAt,
          status: msg.status,
          errorMessage: msg.errorMessage || null,
          type: msg.type,
          readAt: msg.readAt || null,
          threadId: msg.threadId || null,
          author: author
        };
      });

      const collapsedMessages = collapseFailedOutgoingMessages(formattedMessages);
      console.log('Formatted messages:', formattedMessages.length, 'Collapsed:', collapsedMessages.length);
      setMessages(collapsedMessages);
      
      // Обновляем счетчики топиков при загрузке сообщений (для групп)
      if (selectedContact && selectedContact.telegramChatId) {
        const chatIdNum = parseInt(selectedContact.telegramChatId);
        const isGroup = !isNaN(chatIdNum) && chatIdNum < 0;
        if (isGroup) {
          fetchTopics(selectedContact.telegramChatId);
        }
      }
      
      // Отмечаем сообщения как прочитанные после загрузки (если это новый контакт или новый топик)
      if (selectedContact && formattedMessages.length > 0) {
        const contactId = selectedContact.id;
        const topicKey = `${contactId}_${selectedTopicId ?? 'null'}`;
        const lastMarkedKey = `${lastMarkedContactRef.current}_${lastMarkedTopicRef.current ?? 'null'}`;
        
        if (topicKey !== lastMarkedKey) {
          lastMarkedContactRef.current = contactId;
          lastMarkedTopicRef.current = selectedTopicId;
          // Небольшая задержка перед отметкой
          setTimeout(() => {
            markMessagesAsRead(selectedContact);
          }, 300);
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки сообщений:', error);
      console.error('Параметры запроса:', { playerId, chatId, threadId, selectedContact: selectedContact?.name });
      setMessages([]);
    }
  };

  const fetchGroupSettings = async () => {
    try {
      const settings = await apiGet<GroupSettings[]>('/telegram-group-settings');
      const settingsMap = new Map<string, GroupSettings>();
      settings.forEach((s: GroupSettings) => {
        settingsMap.set(s.chatId, s);
      });
      setGroupSettingsMap(settingsMap);
    } catch (error) {
      console.error('Ошибка загрузки настроек групп:', error);
    }
  };

  // Загрузка настроек обновления
  const fetchRefreshSettings = async () => {
    try {
      const settings = await apiGet<Record<string, string>>('/settings?category=chat');
      
      if (settings['chat.contactsRefreshInterval']) {
        const value = parseInt(settings['chat.contactsRefreshInterval'], 10);
        if (!isNaN(value) && value >= 5 && value <= 300) {
          setContactsRefreshInterval(value);
        }
      }
      
      if (settings['chat.messagesRefreshInterval']) {
        const value = parseInt(settings['chat.messagesRefreshInterval'], 10);
        if (!isNaN(value) && value >= 5 && value <= 300) {
          setMessagesRefreshInterval(value);
        }
      }

      if (settings['chat.historyLookbackDays']) {
        const value = parseInt(settings['chat.historyLookbackDays'], 10);
        if (!isNaN(value) && HISTORY_LOOKBACK_OPTIONS.includes(value as (typeof HISTORY_LOOKBACK_OPTIONS)[number])) {
          setHistoryLookbackDays(value);
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек обновления:', error);
    }
  };

  // Сохранение настройки обновления
  const saveRefreshSetting = async (key: string, value: number) => {
    try {
      await apiPost('/settings', {
        key: `chat.${key}`,
        value: String(value),
        category: 'chat'
      });
    } catch (error) {
      console.error(`Ошибка сохранения настройки ${key}:`, error);
    }
  };

  const markMessagesAsRead = React.useCallback(async (contact?: ChatContact) => {
    const contactToMark = contact || selectedContact;
    if (!contactToMark) return;

    try {
      const chatId = contactToMark.telegramChatId;
      if (!chatId) return;

      // Определяем, является ли это группой
      const chatIdNum = chatId ? parseInt(chatId) : NaN;
      const isGroup = !isNaN(chatIdNum) && chatIdNum < 0;
      
      // Определяем threadId для отметки (если это группа и выбран топик)
      const threadId = isGroup ? selectedTopicId : undefined;

      // Для всех чатов (групп и приватных) используем только chatId
      // Это более надежно, так как chatId уникален для каждого чата
      // и не зависит от того, к какому игроку привязаны сообщения
      let requestBody: any;
      if (isGroup) {
        // Для групп: только chatId и threadId
        // threadId может быть null (основной поток) или string (конкретный топик)
        requestBody = {
          chatId: chatId,
          threadId: threadId !== undefined ? (threadId === null ? null : threadId) : undefined
        };
      } else {
        // Для приватных чатов: только chatId (без playerId)
        // Это гарантирует, что все сообщения в этом чате будут отмечены как прочитанные
        requestBody = {
          chatId: chatId
        };
      }
      
      console.log('markMessagesAsRead requestBody:', requestBody);

      // Отмечаем все непрочитанные входящие сообщения как прочитанные
      const result = await apiPost<{ success: boolean; count: number }>('/telegram-messages/mark-read', requestBody);

      console.log('Отмечено сообщений как прочитанных:', result.count);

      // Обновляем счетчики топиков после отметки сообщений как прочитанных
      if (isGroup && chatId) {
        // Обновляем топики, чтобы получить актуальные счетчики
        const topicsResponse = await apiGet<{ topics: GroupTopic[]; mainThreadUnreadCount: number }>(`/telegram-group-settings/${chatId}/topics`);
        setTopics(topicsResponse.topics || []);
        setMainThreadUnreadCount(topicsResponse.mainThreadUnreadCount || 0);
        
        // Пересчитываем общее количество непрочитанных для контакта
        const totalUnread = (topicsResponse.mainThreadUnreadCount || 0) + 
          (topicsResponse.topics || []).reduce((sum, topic) => sum + (topic.unreadCount || 0), 0);
        
        // Обновляем счетчик непрочитанных в локальном состоянии
        setContacts(prevContacts => 
          prevContacts.map(c => {
            if (c.id === contactToMark.id) {
              return { ...c, unreadCount: totalUnread };
            }
            return c;
          })
        );
      } else {
        // Для приватных чатов просто обнуляем счетчик
        setContacts(prevContacts => 
          prevContacts.map(c => {
            if (c.id === contactToMark.id) {
              return { ...c, unreadCount: 0 };
            }
            return c;
          })
        );
      }
    } catch (error) {
      console.error('Ошибка отметки сообщений как прочитанных:', error);
    }
  }, [selectedContact, selectedTopicId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Функции для форматирования текста
  const insertTextAtCursor = (before: string, after: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = newMessage.substring(start, end);
    const newText = newMessage.substring(0, start) + before + selectedText + after + newMessage.substring(end);
    
    setNewMessage(newText);
    
    // Устанавливаем курсор после вставленного текста
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length + after.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const formatBold = () => insertTextAtCursor('<b>', '</b>');
  const formatItalic = () => insertTextAtCursor('<i>', '</i>');
  const formatUnderline = () => insertTextAtCursor('<u>', '</u>');
  const formatStrikethrough = () => insertTextAtCursor('<s>', '</s>');
  const formatCode = () => insertTextAtCursor('<code>', '</code>');
  const formatPre = () => insertTextAtCursor('<pre>', '</pre>');
  const formatLink = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = newMessage.substring(start, end);
    if (selectedText) {
      insertTextAtCursor('<a href="', `">${selectedText}</a>`);
    } else {
      insertTextAtCursor('<a href="', '">текст</a>');
    }
  };
  const formatBlockquote = () => insertTextAtCursor('<blockquote>', '</blockquote>');
  const formatSpoiler = () => insertTextAtCursor('<span class="tg-spoiler">', '</span>');

  // Обработка выбора эмодзи
  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newText = newMessage.substring(0, start) + emojiData.emoji + newMessage.substring(end);
    
    setNewMessage(newText);
    
    // Устанавливаем курсор после вставленного эмодзи
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + emojiData.emoji.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
    
    setShowEmojiPicker(false);
  };

  // Закрытие панели эмодзи при клике вне её
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showEmojiPicker]);

  // Мемоизируем функцию formatTime
  const formatTime = React.useCallback((dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Вчера';
    } else if (days < 7) {
      return date.toLocaleDateString('ru-RU', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
    }
  }, []);

  // Мемоизируем обработчик выбора контакта
  const handleContactSelect = React.useCallback((contact: ChatContact) => {
    setSelectedContact(contact);
  }, []);

  // Форматирование текста нажатия на кнопку для понятного отображения
  const formatButtonClickText = (buttonData: string): string => {
    if (!buttonData) return 'Нажатие кнопки';
    
    // Меню навигации
    if (buttonData === 'ttm_menu:back') {
      return '◀️ Назад';
    }
    if (buttonData === 'ttm_menu:standings') {
      return '📊 Классификации';
    }
    if (buttonData === 'ttm_menu:tournaments') {
      return '🏆 Турниры';
    }
    if (buttonData === 'ttm_menu:contests') {
      return '🎁 Конкурсы';
    }
    if (buttonData === 'ttm_menu:schedule') {
      return '📅 Календарь';
    }
    if (buttonData === 'ttm_menu:exit') {
      return 'Выход';
    }
    if (buttonData === 'ttm_menu:language') {
      return 'RO/RU';
    }
    if (buttonData === 'ttm_menu:courts') {
      return '🎾 Корты';
    }
    
    // Турниры
    if (buttonData.startsWith('ttm_tournament:')) {
      return '🏆 Выбор турнира';
    }
    if (buttonData.startsWith('ttm_tournament_schedule:')) {
      return '📅 Расписание турнира';
    }
    if (buttonData.startsWith('ttm_tournament_results:')) {
      return '📊 Результаты турнира';
    }
    if (buttonData.startsWith('ttm_tournament_courts:')) {
      return '🎾 Доступные корты';
    }
    if (buttonData === 'ttm_tournaments:active') {
      return '🎾 Активные турниры';
    }
    if (buttonData === 'ttm_tournaments:previous') {
      return '🏆 Предыдущие турниры';
    }
    
    // Уровни и результаты
    if (buttonData.startsWith('ttm_level:')) {
      const parts = buttonData.split(':');
      if (parts.length >= 3) {
        const level = decodeURIComponent(parts[2]);
        return `🏆 Результаты: ${level}`;
      }
      return '🏆 Результаты турниров';
    }
    
    // Турнирные таблицы
    if (buttonData.startsWith('standings_level_')) {
      const parts = buttonData.replace('standings_level_', '').split('_');
      if (parts.length >= 2) {
        const level = decodeURIComponent(parts.slice(1).join('_'));
        return `📊 Турнирные таблицы: ${level}`;
      }
      return '📊 Турнирные таблицы';
    }
    if (buttonData.startsWith('standings_tournament_')) {
      return '📊 Выбор турнира';
    }
    if (buttonData.startsWith('test_standings_level_browser_')) {
      const parts = buttonData.replace('test_standings_level_browser_', '').split('_');
      if (parts.length >= 2) {
        const level = decodeURIComponent(parts.slice(1).join('_'));
        return `🧪 Тест: Турнирные таблицы: ${level}`;
      }
      return '🧪 Тест: Турнирные таблицы';
    }
    
    // Live счет
    if (buttonData.startsWith('livescore_tournament:')) {
      return '⚡ Live счет турнира';
    }
    
    // Слоты и ответы
    if (buttonData.startsWith('ttm_slot_response:')) {
      const parts = buttonData.split(':');
      if (parts.length >= 3) {
        const response = parts[2];
        const responseText = response === 'ACCEPT' ? '✅ Принял' : response === 'REJECT' ? '❌ Отклонил' : response === 'MAYBE' ? '🤔 Возможно' : response;
        return `📅 Слот: ${responseText}`;
      }
      return '📅 Ответ на слот';
    }
    
    // Голосования
    if (buttonData.startsWith('poll_vote:')) {
      const parts = buttonData.split(':');
      if (parts.length >= 3) {
        return `📊 Голосование: вариант ${parts[2]}`;
      }
      return '📊 Голосование';
    }
    
    // Ввод счета
    if (buttonData.startsWith('score_')) {
      return '🎾 Ввод счета';
    }
    if (buttonData.startsWith('score_match_')) {
      return '🎾 Ввод счета матча';
    }
    
    // Тестовые команды
    if (buttonData === 'test_back' || buttonData === 'back') {
      return '◀️ Назад';
    }
    
    // Если не распознано, показываем первые 50 символов
    return buttonData.length > 50 ? buttonData.substring(0, 50) + '...' : buttonData;
  };

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    
    // Проверяем, является ли дата сегодняшней
    const isToday = date.getDate() === now.getDate() &&
                    date.getMonth() === now.getMonth() &&
                    date.getFullYear() === now.getFullYear();
    
    if (isToday) {
      // Если сегодня - показываем только время
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    } else {
      // Если не сегодня - показываем дату и время
      const dateStr = date.toLocaleDateString('ru-RU', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric'
      });
      const timeStr = date.toLocaleTimeString('ru-RU', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      return `${dateStr} ${timeStr}`;
    }
  };

  // Мемоизируем отфильтрованные контакты
  const filteredContacts = React.useMemo(() => {
    return contacts.filter(contact =>
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (contact.telegramUsername && contact.telegramUsername.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [contacts, searchQuery]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedContact) return;

    const messageText = newMessage.trim();
    setNewMessage(''); // Очищаем поле ввода сразу для лучшего UX

    // Оптимистично добавляем сообщение в UI
    const optimisticMessage: Message = {
      id: `temp-${Date.now()}`,
      text: messageText,
      direction: 'OUTGOING',
      createdAt: new Date().toISOString(),
      status: 'SENT'
    };

    setMessages([...messages, optimisticMessage]);
    scrollToBottom();

    try {
      // Определяем playerId и chatId для отправки
      let playerId: string | undefined;
      let chatId: string | undefined;
      
      // Определяем, является ли это группой
      const chatIdForSend = selectedContact.telegramChatId;
      const chatIdNum = chatIdForSend ? parseInt(chatIdForSend) : NaN;
      const isGroup = !isNaN(chatIdNum) && chatIdNum < 0;
      
      // Определяем threadId для групп
      let threadId: string | null | undefined = undefined;
      if (isGroup) {
        threadId = selectedTopicId; // null для основного потока, string для топика
      }

      if (selectedContact.isUnregistered) {
        // Для незарегистрированных пользователей используем originalPlayerId и chatId
        playerId = selectedContact.originalPlayerId;
        chatId = selectedContact.telegramChatId || undefined;
      } else {
        // Для привязанных аккаунтов используем originalPlayerId (если есть) или извлекаем из id
        if (selectedContact.originalPlayerId) {
          playerId = selectedContact.originalPlayerId;
        } else if (selectedContact.id.includes('_')) {
          playerId = selectedContact.id.split('_')[0];
        } else {
          playerId = selectedContact.id;
        }
        chatId = selectedContact.telegramChatId || undefined;
      }

      // Отправляем сообщение через API
      await apiPost('/telegram-messages/send', {
        playerId: playerId,
        chatId: chatId,
        text: messageText,
        threadId: threadId !== undefined ? threadId : undefined
      });

      // Обновляем сообщения, чтобы получить реальное сообщение с сервера
      if (selectedContact) {
        // Определяем threadId для обновления сообщений
        const chatIdForRefresh = selectedContact.telegramChatId;
        const chatIdNumForRefresh = chatIdForRefresh ? parseInt(chatIdForRefresh) : NaN;
        const isGroupForRefresh = !isNaN(chatIdNumForRefresh) && chatIdNumForRefresh < 0;
        const threadIdForRefresh = isGroupForRefresh ? selectedTopicId : undefined;
        
        let playerIdForRefresh = selectedContact.id;
        if (selectedContact.originalPlayerId) {
          playerIdForRefresh = selectedContact.originalPlayerId;
        } else if (selectedContact.id.includes('_')) {
          playerIdForRefresh = selectedContact.id.split('_')[0];
        }
        
        await fetchMessages(playerIdForRefresh, chatIdForRefresh || undefined, threadIdForRefresh);
      }
    } catch (error: any) {
      console.error('Ошибка отправки сообщения:', error);
      
      // Удаляем оптимистичное сообщение и показываем ошибку
      setMessages(messages);
      setNewMessage(messageText); // Возвращаем текст в поле ввода
      
      // Можно добавить уведомление об ошибке
      alert(`Ошибка отправки сообщения: ${error.message || 'Неизвестная ошибка'}`);
    }
  };

  return (
    <div className="flex bg-gray-100 overflow-hidden" style={{ height: '100%' }}>
      {/* Боковая панель со списком чатов */}
      <div className="w-96 bg-white border-r border-gray-200 flex flex-col shadow-sm overflow-hidden">
        {/* Поиск */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Поиск"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => setShowRefreshSettingsModal(true)}
              className="p-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center"
              title="Настройки обновления"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </button>
            <button
              onClick={async () => {
                setRefreshing(true);
                await fetchGroupSettings();
                if (selectedContact) {
                  await Promise.all([
                    fetchContacts(),
                    fetchMessages(selectedContact.id, selectedContact.telegramChatId || undefined)
                  ]);
                } else {
                  await fetchContacts();
                }
                setRefreshing(false);
              }}
              disabled={refreshing}
              className="p-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              title="Обновить"
            >
              <svg
                className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Список контактов */}
        <div ref={contactsListRef} className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-500">Загрузка...</div>
          ) : filteredContacts.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {searchQuery ? 'Контакты не найдены' : 'Нет контактов с Telegram'}
            </div>
          ) : (
            filteredContacts.map((contact) => (
              <ChatContactItem
                key={contact.id}
                contact={contact}
                isSelected={selectedContact?.id === contact.id}
                onSelect={handleContactSelect}
                formatTime={formatTime}
              />
            ))
          )}
        </div>
      </div>

      {/* Основная область с чатом */}
      <div className="flex-1 flex flex-col bg-white overflow-hidden">
        {selectedContact ? (
          <>
            {/* Заголовок чата */}
            <div className="h-16 border-b border-gray-200 flex items-center px-6 bg-gray-50">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold mr-3">
                {selectedContact.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <h2 className="font-medium text-sm text-gray-900">{selectedContact.name}</h2>
                {/* Показываем username только для незарегистрированных пользователей */}
                {selectedContact.isUnregistered && selectedContact.telegramUsername && (
                  <p className="text-xs text-gray-500">@{selectedContact.telegramUsername.replace('@', '')}</p>
                )}
                {selectedContact.isUnregistered && selectedContact.telegramChatId && (
                  <p className="text-xs text-gray-500">Chat ID: {selectedContact.telegramChatId}</p>
                )}
              </div>
            </div>

            {/* Табы с топиками (только для групп) */}
            {selectedContact.telegramChatId && (() => {
              const chatIdNum = parseInt(selectedContact.telegramChatId);
              const isGroup = !isNaN(chatIdNum) && chatIdNum < 0;
              
              if (isGroup && topics.length > 0) {
                return (
                  <div className="border-b border-gray-200 bg-white px-4 overflow-x-auto">
                    <div className="flex gap-2 py-2">
                      {/* Таб основного потока */}
                      <button
                        onClick={() => setSelectedTopicId(null)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors flex items-center gap-2 ${
                          selectedTopicId === null
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span>Основной поток</span>
                        {mainThreadUnreadCount > 0 && (
                          <span className="bg-blue-500 text-white text-xs font-semibold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                            {mainThreadUnreadCount > 99 ? '99+' : mainThreadUnreadCount}
                          </span>
                        )}
                      </button>
                      
                      {/* Табы топиков */}
                      {topics.map((topic) => (
                        <button
                          key={topic.id}
                          onClick={() => setSelectedTopicId(topic.threadId)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors flex items-center gap-2 ${
                            selectedTopicId === topic.threadId
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <span>{topic.name || `Топик ${topic.threadId}`}</span>
                          {(topic.unreadCount ?? 0) > 0 && (
                            <span className="bg-blue-500 text-white text-xs font-semibold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                              {topic.unreadCount! > 99 ? '99+' : topic.unreadCount}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Область сообщений */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50" style={{ 
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'grid\' width=\'60\' height=\'60\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M 60 0 L 0 0 0 60\' fill=\'none\' stroke=\'%23d1d5db\' stroke-width=\'0.5\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100%25\' height=\'100%25\' fill=\'url(%23grid)\'/%3E%3C/svg%3E")',
              backgroundSize: '60px 60px'
            }}>
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 mt-8">
                  <p className="text-sm">Нет сообщений в этом чате</p>
                  <p className="text-xs mt-2">Сообщения появятся здесь после общения через Telegram бота</p>
                </div>
              ) : (
                messages.map((message) => {
                  // Определяем, является ли это первым непрочитанным сообщением
                  const isFirstUnread = firstUnreadMessageIdRef.current === message.id;
                  const normalizedStatus = String(message.status || '').trim().toUpperCase();
                  const isFailedOutgoing = message.direction === 'OUTGOING' && normalizedStatus === 'FAILED';
                  
                  return (
                  <div
                    key={message.id}
                    ref={isFirstUnread ? firstUnreadRef : null}
                    className={`flex mb-4 ${
                      message.direction === 'OUTGOING' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-md rounded-2xl px-4 py-2 shadow-sm ${
                        isFailedOutgoing
                          ? 'bg-red-50 text-red-900 border border-red-300 rounded-br-sm'
                          : message.direction === 'OUTGOING'
                          ? 'bg-blue-500 text-white rounded-br-sm'
                          : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm'
                      }`}
                    >
                      {/* Автор сообщения (для входящих сообщений в группах) */}
                      {message.direction === 'INCOMING' && message.author && (
                        <div className="text-xs mb-1 font-medium text-gray-600">
                          {/* Приоритет: 1. Имя игрока из базы (если не системный), 2. Имя и фамилия из Telegram + @username в скобках, 3. "Неизвестный" */}
                          {(message.author.playerName && message.author.playerName !== 'Незарегистрированные пользователи (Telegram)') ? message.author.playerName :
                           (() => {
                             // Формируем имя из Telegram
                             let name = '';
                             if (message.author.firstName && message.author.lastName) {
                               name = `${message.author.firstName} ${message.author.lastName}`;
                             } else if (message.author.firstName) {
                               name = message.author.firstName;
                             } else if (message.author.lastName) {
                               name = message.author.lastName;
                             }
                             
                             // Добавляем username в скобках, если есть
                             if (name && message.author.username) {
                               return `${name} (@${message.author.username})`;
                             } else if (name) {
                               return name;
                             } else if (message.author.username) {
                               return `@${message.author.username}`;
                             } else {
                               return 'Неизвестный';
                             }
                           })()}
                        </div>
                      )}
                      {/* Тип сообщения (если это не обычное текстовое сообщение) */}
                      {message.type && message.type !== 'TEXT_MESSAGE' && message.type !== 'NOTIFICATION' && message.type !== 'BUTTON_CLICK' && (
                        <div className={`text-xs mb-1 ${
                          isFailedOutgoing
                            ? 'text-red-700'
                            : message.direction === 'OUTGOING'
                              ? 'text-blue-100'
                              : 'text-gray-500'
                        }`}>
                          {message.type === 'COMMAND' ? 'Команда' : 
                           message.type === 'REQUEST' ? 'Запрос' :
                           message.type === 'RESPONSE' ? 'Ответ' : message.type}
                        </div>
                      )}
                      {message.type === 'BUTTON_CLICK' ? (
                        <div className={`text-sm ${
                          isFailedOutgoing
                            ? 'text-red-900'
                            : message.direction === 'OUTGOING'
                              ? 'text-white'
                              : 'text-gray-800'
                        }`}>
                          {formatButtonClickText(message.text || '')}
                        </div>
                      ) : (
                        <FormattedMessage 
                          text={message.text || '(пустое сообщение)'} 
                          isOutgoing={message.direction === 'OUTGOING'}
                        />
                      )}
                      {isFailedOutgoing && (
                        <div className="mt-1.5 text-xs text-red-700 border-t border-red-200 pt-1">
                          <div>
                            Не доставлено
                            {message.failedAttempts && message.failedAttempts > 1 ? ` (попыток: ${message.failedAttempts})` : ''}
                          </div>
                          {message.errorMessage && (
                            <div className="mt-0.5 break-words">Ошибка: {message.errorMessage}</div>
                          )}
                        </div>
                      )}
                      <div className={`flex items-center justify-end mt-1 ${
                        isFailedOutgoing
                          ? 'text-red-600'
                          : message.direction === 'OUTGOING'
                            ? 'text-blue-100'
                            : 'text-gray-400'
                      }`}>
                        <span className="text-xs">{formatMessageTime(message.createdAt)}</span>
                        {message.direction === 'OUTGOING' && message.status && (
                          <span className="ml-1 text-xs">
                            {normalizedStatus === 'FAILED' ? '✖' :
                             normalizedStatus === 'SENT' ? '✓' : 
                             normalizedStatus === 'DELIVERED' ? '✓✓' : 
                             normalizedStatus === 'READ' ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Поле ввода сообщения с предпросмотром */}
            <div className="border-t border-gray-200 bg-white">
              {/* Предпросмотр форматированного текста */}
              {newMessage.trim() && (
                <div className="px-6 pt-3 pb-2 border-b border-gray-200 bg-gray-50">
                  <div className="text-xs text-gray-500 mb-1">Предпросмотр:</div>
                  <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 min-h-[40px] max-h-[120px] overflow-y-auto">
                    <FormattedMessage text={newMessage} />
                  </div>
                </div>
              )}
              
              {/* Кнопки форматирования */}
              <div className="px-6 pt-2 pb-1 flex items-center gap-1 border-b border-gray-100 flex-wrap">
                <button
                  onClick={formatBold}
                  type="button"
                  className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900 transition-colors"
                  title="Жирный (Ctrl+B)"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 4a1 1 0 011 1v10a1 1 0 01-2 0V5a1 1 0 011-1zm4 0h3a3 3 0 010 6h-2a1 1 0 00-.83.44L8.5 12H12a1 1 0 110 2H7a1 1 0 01-1-1V5a1 1 0 011-1zm2 2v2h2a1 1 0 100-2H11z" />
                  </svg>
                </button>
                <button
                  onClick={formatItalic}
                  type="button"
                  className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900 transition-colors"
                  title="Курсив (Ctrl+I)"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 2a1 1 0 000 2h2l-2 8H6a1 1 0 100 2h8a1 1 0 100-2h-2l2-8h2a1 1 0 100-2H8z" />
                  </svg>
                </button>
                <button
                  onClick={formatUnderline}
                  type="button"
                  className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900 transition-colors"
                  title="Подчеркнутый (Ctrl+U)"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19h14M5 5h14" />
                  </svg>
                </button>
                <button
                  onClick={formatStrikethrough}
                  type="button"
                  className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900 transition-colors"
                  title="Зачеркнутый"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </button>
                <button
                  onClick={formatCode}
                  type="button"
                  className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900 transition-colors"
                  title="Моношрифт"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </button>
                <button
                  onClick={formatPre}
                  type="button"
                  className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900 transition-colors"
                  title="Блок кода"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  onClick={formatLink}
                  type="button"
                  className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900 transition-colors"
                  title="Ссылка"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </button>
                <button
                  onClick={formatBlockquote}
                  type="button"
                  className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900 transition-colors"
                  title="Цитата"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </button>
                <button
                  onClick={formatSpoiler}
                  type="button"
                  className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900 transition-colors"
                  title="Спойлер"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                </button>
                <div className="flex-1" />
                <div className="relative" ref={emojiPickerRef}>
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    type="button"
                    className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900 transition-colors"
                    title="Эмодзи"
                  >
                    <span className="text-lg">😀</span>
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute bottom-full right-0 mb-2 z-50">
                      <EmojiPicker
                        onEmojiClick={handleEmojiClick}
                        width={350}
                        height={400}
                        previewConfig={{ showPreview: false }}
                        skinTonesDisabled
                        searchDisabled={false}
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Поле ввода */}
              <div className="flex items-center px-6 py-3">
                <textarea
                  ref={textareaRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                    // Горячие клавиши для форматирования
                    if (e.ctrlKey || e.metaKey) {
                      if (e.key === 'b') {
                        e.preventDefault();
                        formatBold();
                      } else if (e.key === 'i') {
                        e.preventDefault();
                        formatItalic();
                      } else if (e.key === 'u') {
                        e.preventDefault();
                        formatUnderline();
                      }
                    }
                  }}
                  placeholder="Введите сообщение... (поддерживается HTML: <b>жирный</b>, <i>курсив</i>, <code>код</code>, эмодзи 👋)"
                  rows={1}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mr-3 resize-none min-h-[40px] max-h-[120px]"
                  style={{ 
                    height: 'auto',
                    minHeight: '40px'
                  }}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Отправить
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="text-6xl mb-4">💬</div>
              <h2 className="text-lg font-medium text-gray-700 mb-2">Выберите чат</h2>
              <p className="text-sm text-gray-500">Выберите контакт из списка слева, чтобы начать переписку</p>
            </div>
          </div>
        )}
      </div>

      {/* Модальное окно настроек обновления */}
      {showRefreshSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowRefreshSettingsModal(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Настройки обновления</h2>
              <button
                onClick={() => setShowRefreshSettingsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Интервал обновления контактов (секунды)
                </label>
                <input
                  type="number"
                  min="5"
                  max="300"
                  value={contactsRefreshInterval}
                  onChange={(e) => {
                    const value = Math.max(5, Math.min(300, parseInt(e.target.value) || 30));
                    setContactsRefreshInterval(value);
                    saveRefreshSetting('contactsRefreshInterval', value);
                  }}
                  onBlur={(e) => {
                    const value = Math.max(5, Math.min(300, parseInt(e.target.value) || 30));
                    setContactsRefreshInterval(value);
                    saveRefreshSetting('contactsRefreshInterval', value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">Минимум: 5 сек, Максимум: 300 сек</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Интервал обновления сообщений (секунды)
                </label>
                <input
                  type="number"
                  min="5"
                  max="300"
                  value={messagesRefreshInterval}
                  onChange={(e) => {
                    const value = Math.max(5, Math.min(300, parseInt(e.target.value) || 10));
                    setMessagesRefreshInterval(value);
                    saveRefreshSetting('messagesRefreshInterval', value);
                  }}
                  onBlur={(e) => {
                    const value = Math.max(5, Math.min(300, parseInt(e.target.value) || 10));
                    setMessagesRefreshInterval(value);
                    saveRefreshSetting('messagesRefreshInterval', value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">Минимум: 5 сек, Максимум: 300 сек</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Период истории чата
                </label>
                <select
                  value={historyLookbackDays}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (HISTORY_LOOKBACK_OPTIONS.includes(value as (typeof HISTORY_LOOKBACK_OPTIONS)[number])) {
                      setHistoryLookbackDays(value);
                      saveRefreshSetting('historyLookbackDays', value);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {HISTORY_LOOKBACK_OPTIONS.map((days) => (
                    <option key={days} value={days}>
                      {days} дней
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Загружаются сообщения только за выбранный период
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowRefreshSettingsModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;

